/**
 * Auth API service (staff self-signup, login base URL for consistency)
 */
// const API_BASE = 'https://medzapidev.eshatechgroup.com';

const STAFF_SELF_SIGNUP_POWER_AUTOMATE_URL =
    'https://a4fce81eef4be4889ed4066d1378ef.d4.environment.api.powerplatform.com:443/powerautomate/automations/direct/workflows/72ea635a5fab40abb3a7c93ab6c3952e/triggers/manual/paths/invoke?api-version=1&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=yD1UPs5ptbO0HXGafLFzlCulqPt0VK1xdlfTLr2rABk';
// const API_BASE = 'https://medzhubstageapi.eshatechgroup.com';
const API_BASE = 'https://medzhubapi.eshatechgroup.com';
// const API_BASE = 'http://192.168.0.11:3001';

/**
 * Staff self-signup — POST /api/auth/staff-self-signup
 * @param {Object} payload
 * @param {string} payload.name - Required
 * @param {string} payload.email - Required
 * @param {string} payload.contact - Required
 * @param {string} [payload.specialization]
 * @param {number} [payload.experience_years]
 * @param {number} [payload.base_latitude]
 * @param {number} [payload.base_longitude]
 * @returns {Promise<{ message, rehabStaff, user }>}
 * @throws {Error} with message from backend (validation, "Email already exists", etc.)
 */
export async function staffSelfSignup(payload) {
    const res = await fetch(`${API_BASE}/api/auth/staff-self-signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            name: payload.name,
            email: payload.email,
            contact: payload.contact,
            ...(payload.specialization != null && payload.specialization !== '' && { specialization: payload.specialization }),
            ...(payload.experience_years != null && { experience_years: payload.experience_years }),
            ...(payload.base_latitude != null && { base_latitude: payload.base_latitude }),
            ...(payload.base_longitude != null && { base_longitude: payload.base_longitude }),
        }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        const message = data.error || data.errors?.[0]?.msg || 'Signup failed';
        throw Object.assign(new Error(message), { status: res.status, data });
    }
    return data;
}

/**
 * Notify Power Automate when a therapist completes self-signup.
 * Non-blocking — callers should not fail signup if this request errors.
 * @param {Object} payload - Power Automate schema (not the staff-self-signup API shape)
 * @param {string} payload.fullName
 * @param {string} payload.mobileNumber
 * @param {string} payload.emailAddress
 * @param {string} payload.specialisation
 * @param {string} [payload.otherSpecialisation]
 * @param {number} payload.yearsOfExperience
 * @param {string} payload.city
 */
export async function triggerStaffSelfSignupAutomation(payload) {
    const res = await fetch(STAFF_SELF_SIGNUP_POWER_AUTOMATE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });
    if (!res.ok) {
        throw new Error(`Power Automate trigger failed (${res.status})`);
    }
}

/**
 * Book a demo — POST /api/demo-requests/create
 * @param {Object} payload
 * @param {string} payload.name
 * @param {string} payload.phone
 * @param {string} payload.centreName
 * @param {string} payload.city
 * @param {string} payload.therapistCount
 * @param {string[]} payload.painPoints - e.g. visibility, sessionVerification, documentation, payoutDisputes
 * @returns {Promise<unknown>}
 */
export async function createDemoRequest(payload) {
    const res = await fetch(`${API_BASE}/api/demo-requests/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            name: payload.name,
            phone: payload.phone,
            centreName: payload.centreName,
            city: payload.city,
            therapistCount: payload.therapistCount,
            painPoints: payload.painPoints,
        }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        const message = data.error || data.errors?.[0]?.msg || data.message || 'Could not submit demo request';
        throw Object.assign(new Error(message), { status: res.status, data });
    }
    return data;
}

/**
 * Contact us — POST /api/contacts/create
 * @param {Object} payload
 * @param {string} payload.name
 * @param {string} payload.phone
 * @param {string} payload.reachingOutAs
 * @param {string} payload.message
 * @returns {Promise<unknown>}
 */
export async function createContact(payload) {
    const res = await fetch(`${API_BASE}/api/contacts/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            name: payload.name,
            phone: payload.phone,
            reachingOutAs: payload.reachingOutAs,
            message: payload.message,
        }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        const message = data.error || data.errors?.[0]?.msg || data.message || 'Could not submit contact request';
        throw Object.assign(new Error(message), { status: res.status, data });
    }
    return data;
}
